const OVERPASS_URL = 'https://overpass-api.de/api/interpreter';

const TYPE_MAP = {
  services:     { emoji: '⛽', color: '#3b82f6', labelPt: 'Área de Serviço',    labelEn: 'Service Area' },
  fuelStation:  { emoji: '⛽', color: '#ef4444', labelPt: 'Posto de Combustível', labelEn: 'Fuel Station' },
  restArea:     { emoji: '😴', color: '#8b5cf6', labelPt: 'Local de Descanso',  labelEn: 'Rest Area' },
  truckParking: { emoji: '🅿',  color: '#f59e0b', labelPt: 'Parque p/ Camiões', labelEn: 'Truck Parking' },
  hotel:        { emoji: '🏨', color: '#8b5cf6', labelPt: 'Hotel / Motel',      labelEn: 'Hotel / Motel' },
};

export { TYPE_MAP };

function haversineKm([lng1, lat1], [lng2, lat2]) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// Pre-compute cumulative distance array
function buildCumDists(coords) {
  const dists = [0];
  for (let i = 1; i < coords.length; i++) {
    dists.push(dists[i - 1] + haversineKm(coords[i - 1], coords[i]));
  }
  return dists;
}

// Sample route to N evenly-spaced points
function sampleCoords(coords, maxPoints = 40) {
  if (coords.length <= maxPoints) return coords;
  const step = (coords.length - 1) / (maxPoints - 1);
  const sampled = [];
  for (let i = 0; i < maxPoints; i++) {
    sampled.push(coords[Math.round(i * step)]);
  }
  return sampled;
}

// Split route coords into segments of ~segmentKm length
function splitRoute(coords, segmentKm = 200) {
  const segments = [];
  let current = [coords[0]];
  let dist = 0;

  for (let i = 1; i < coords.length; i++) {
    dist += haversineKm(coords[i - 1], coords[i]);
    current.push(coords[i]);
    if (dist >= segmentKm) {
      segments.push(current);
      current = [coords[i]]; // overlap: start next segment from last point
      dist = 0;
    }
  }
  if (current.length > 1) segments.push(current);
  return segments;
}

function getBbox(coords, paddingDeg = 0.06) {
  let minLng = Infinity, minLat = Infinity, maxLng = -Infinity, maxLat = -Infinity;
  for (const [lng, lat] of coords) {
    if (lng < minLng) minLng = lng;
    if (lat < minLat) minLat = lat;
    if (lng > maxLng) maxLng = lng;
    if (lat > maxLat) maxLat = lat;
  }
  return `${minLat - paddingDeg},${minLng - paddingDeg},${maxLat + paddingDeg},${maxLng + paddingDeg}`;
}

function buildQuery(bbox) {
  return `
[out:json][timeout:20];
(
  node["highway"="services"](${bbox});
  way["highway"="services"](${bbox});
  node["highway"="rest_area"](${bbox});
  way["highway"="rest_area"](${bbox});
  node["amenity"="parking"]["hgv"~"yes|designated"](${bbox});
  node["amenity"="truck_stop"](${bbox});
  node["amenity"="fuel"](${bbox});
  node["tourism"~"hotel|motel"](${bbox});
);
out center;
`.trim();
}

function classifyElement(el) {
  const tags = el.tags || {};
  if (tags.highway === 'services') return 'services';
  if (tags.highway === 'rest_area') return 'restArea';
  if (tags.amenity === 'truck_stop') return 'truckParking';
  if (tags.amenity === 'parking' && (tags.hgv === 'yes' || tags.hgv === 'designated')) return 'truckParking';
  if (tags.amenity === 'fuel') return 'fuelStation';
  if (tags.tourism === 'hotel' || tags.tourism === 'motel') return 'hotel';
  return null;
}

function getCoordinates(el) {
  if (el.type === 'node') return [el.lon, el.lat];
  if (el.center) return [el.center.lon, el.center.lat];
  return null;
}

// Fetch one segment's POIs
async function fetchSegment(segmentCoords) {
  const bbox = getBbox(segmentCoords);
  const query = buildQuery(bbox);

  const res = await fetch(OVERPASS_URL, {
    method: 'POST',
    body: query,
    headers: { 'Content-Type': 'text/plain' },
  });

  if (!res.ok) throw new Error(`Overpass error: ${res.status}`);
  return res.json();
}

// Distance from POI to nearest sampled route point + km along route
function distToRouteInfo(poiCoord, routeCoords, cumDists) {
  let minDist = Infinity;
  let closestIdx = 0;
  for (let i = 0; i < routeCoords.length; i++) {
    const d = haversineKm(poiCoord, routeCoords[i]);
    if (d < minDist) { minDist = d; closestIdx = i; }
  }
  return { dist: minDist, kmAlongRoute: cumDists[closestIdx] };
}

export async function fetchPoisAlongRoute(routeCoordinates, maxDistKm = 5) {
  // Sample route for distance calculations
  const sampled = sampleCoords(routeCoordinates, 120);
  const cumDists = buildCumDists(sampled);

  // Split route into ~200km segments for smaller Overpass queries
  const segments = splitRoute(sampleCoords(routeCoordinates, 300), 200);

  // Fetch all segments in parallel (max 3 concurrent to be polite to Overpass)
  const allElements = [];
  for (let i = 0; i < segments.length; i += 3) {
    const batch = segments.slice(i, i + 3);
    const results = await Promise.allSettled(batch.map(seg => fetchSegment(seg)));
    for (const r of results) {
      if (r.status === 'fulfilled' && r.value?.elements) {
        allElements.push(...r.value.elements);
      }
    }
  }

  // Process and deduplicate
  const pois = [];
  const seen = new Set();

  for (const el of allElements) {
    const type = classifyElement(el);
    if (!type) continue;

    const coords = getCoordinates(el);
    if (!coords) continue;

    const key = `${Math.round(coords[0] * 1000)},${Math.round(coords[1] * 1000)}`;
    if (seen.has(key)) continue;
    seen.add(key);

    const info = distToRouteInfo(coords, sampled, cumDists);
    if (info.dist > maxDistKm) continue;

    const tags = el.tags || {};
    const name = tags.name || tags['name:pt'] || tags['name:en'] || TYPE_MAP[type].labelPt;

    pois.push({
      id: `${el.type}-${el.id}`,
      type,
      name,
      coordinates: coords,
      tags,
      distKm: Math.round(info.dist * 10) / 10,
      kmAlongRoute: Math.round(info.kmAlongRoute),
    });
  }

  pois.sort((a, b) => a.kmAlongRoute - b.kmAlongRoute);
  return pois;
}
